import * as React from 'react'
import upperFirst from 'lodash/upperFirst'
import { Box, Tooltip, Typography } from '@applift/factor'

import { CreativeInfo } from '../../models/Creative'

import styles from './styles.module.scss'

export const EMPTY_VALUE = '-'

export const isUrlValid = (url: string) => {
  try {
    // eslint-disable-next-line
    const validUrl = new URL(url)
    return true
  } catch (err) {
    return false
  }
}

export type DetailsMapper = Array<{
  key: string
  value: string | ((data: CreativeInfo | null) => React.ReactNode)
}>

export interface TrackingUrlsType {
  impressionURL: string
  startUrl: string
  firstQuartileURL: string
  midPointURL: string
  thirdQuartileURL: string
  completeURL: string
}

const getClickUrl = (data: CreativeInfo | null) =>
  data?.clickUrl ? (
    <a
      href={data.clickUrl}
      target="_blank"
      rel="noopener noreferrer"
      style={{ textDecoration: 'none' }}
    >
      <Typography
        sx={{ textColor: 'primary-500', wordBreak: 'break-all' }}
        style={{ whiteSpace: 'pre-wrap' }}
      >
        {data.clickUrl}
      </Typography>
    </a>
  ) : (
    EMPTY_VALUE
  )

const getPixelUrl = (data: CreativeInfo | null) =>
  data?.pixelUrls?.length ? (
    <a
      href={data.pixelUrls[0]}
      target="_blank"
      rel="noopener noreferrer"
      style={{ textDecoration: 'none' }}
    >
      <Typography
        sx={{ textColor: 'primary-500', wordBreak: 'break-all' }}
        style={{ whiteSpace: 'pre-wrap' }}
      >
        {data.pixelUrls[0]}
      </Typography>
    </a>
  ) : (
    EMPTY_VALUE
  )

const getDimensions = (data: CreativeInfo | null) =>
  data?.width && data?.height
    ? `${data.width}px X ${data.height}px`
    : EMPTY_VALUE

const getTitle = (data: CreativeInfo | null) =>
  data?.nativeCreativeAttributes?.title
    ? data?.nativeCreativeAttributes?.title
    : EMPTY_VALUE

const getDescription = (data: CreativeInfo | null) =>
  data?.nativeCreativeAttributes?.description
    ? data?.nativeCreativeAttributes?.description
    : EMPTY_VALUE

const getCTA = (data: CreativeInfo | null) =>
  data?.nativeCreativeAttributes?.cta.ctaText
    ? data?.nativeCreativeAttributes?.cta.ctaText
    : EMPTY_VALUE

const getBrandName = (data: CreativeInfo | null) =>
  data?.nativeCreativeAttributes?.brandName
    ? data?.nativeCreativeAttributes?.brandName
    : EMPTY_VALUE

const getBrandIcon = (data: CreativeInfo | null) =>
  data?.nativeCreativeAttributes?.brandName ? (
    <img
      className={styles.brandIconDetailImage}
      src={data?.nativeCreativeAttributes?.brandIconSource}
      alt="Brand"
    />
  ) : (
    EMPTY_VALUE
  )

const getDuration = (data: CreativeInfo | null) =>
  data?.duration ? `${data.duration}s` : EMPTY_VALUE

const getXmlType = (data: CreativeInfo | null) =>
  data?.xmlType ? upperFirst(data.xmlType) : EMPTY_VALUE

const getXmlVersion = (data: CreativeInfo | null) =>
  data?.xmlVersion ? upperFirst(data.xmlVersion) : EMPTY_VALUE

const getMainImage = (data: CreativeInfo | null) =>
  data?.creativeCardSource ? (
    <img
      className={styles.creativeMainImage}
      src={data.creativeCardSource}
      alt="Creative Thumbnail"
    />
  ) : (
    EMPTY_VALUE
  )

const getTrackingUrls =
  // eslint-disable-next-line
  (target: keyof TrackingUrlsType) => (data: CreativeInfo | null) => {
    if (!data?.audioVideoTrackingData) {
      return EMPTY_VALUE
    }
    const trackingUrls: TrackingUrlsType = JSON.parse(
      data?.audioVideoTrackingData as string
    )
    return (
      <Tooltip title={trackingUrls[target]} arrow placement="top">
        <Box>{trackingUrls[target]}</Box>
      </Tooltip>
    )
  }

export const videoDetailsMapper = [
  // { key: 'Campaign Name', value: 'campaignName' },
  { key: 'Duration', value: getDuration },
  { key: 'VAST Version', value: 'xmlVersion' },
  { key: 'VAST Type', value: getXmlType },
  { key: 'Click URL', value: getClickUrl },
  { key: 'Ad Placement', value: 'adPlacement' },
]

export const imageDetailsMapper = [
  // { key: 'Campaign Name', value: 'campaignName' },
  { key: 'Dimensions', value: getDimensions },
  { key: 'Click URL', value: getClickUrl },
  { key: 'Pixel URL', value: getPixelUrl },
]

export const nativeDetailsMapper = [
  // { key: 'Campaign Name', value: 'campaignName' },
  { key: 'Title', value: getTitle },
  { key: 'Description', value: getDescription },
  { key: 'Click URL', value: getClickUrl },
  { key: 'CTA', value: getCTA },
  { key: 'Pixel URL', value: getPixelUrl },
  { key: 'Brand Name', value: getBrandName },
  { key: 'Brand Icon', value: getBrandIcon },
]

export const nativeImageDetailsMapper = [
  { key: 'Dimensions', value: getDimensions },
]
export const nativeVideoDetailsMapper = [
  { key: 'Duration', value: getDuration },
  { key: 'VAST Version', value: getXmlVersion },
  { key: 'VAST Type', value: getXmlType },
  { key: 'Main Image(only for video)', value: getMainImage },
  { key: 'Impression Tracking URL', value: getTrackingUrls('impressionURL') },
  { key: 'Start Tracking URL', value: getTrackingUrls('startUrl') },
  {
    key: '1st Quartile Tracking URL',
    value: getTrackingUrls('firstQuartileURL'),
  },
  { key: 'Midpoint Tracking URL', value: getTrackingUrls('midPointURL') },
  {
    key: '3rd Quartile Tracking URL',
    value: getTrackingUrls('thirdQuartileURL'),
  },
  { key: 'Complete Tracking URL', value: getTrackingUrls('completeURL') },
]

export const htmlDetailsMapper = [
  // { key: 'Campaign Name', value: 'campaignName' },
  { key: 'Dimensions', value: getDimensions },
  { key: 'Click URL', value: getClickUrl },
]

export const audioDetailsMapper = [
  // { key: 'Campaign Name', value: 'campaignName' },
  { key: 'Duration', value: getDuration },
  { key: 'DAAST Version', value: 'xmlVersion' },
  { key: 'DAAST Type', value: getXmlType },
  { key: 'Click URL', value: getClickUrl },
]
